import {CellProperties} from "handsontable/settings";
import { TVZHotTableSummaryFunction } from "./VZHotTable";


export interface IVZHotTableCellConfigOptions {
    format?: "percent" | "currency" | "count" | "string" | "html" | ((cellProperties: CellProperties) => void);
    isReadOnly?: boolean;
    isOverrideValue?: boolean;
    isRowHeader?: boolean;
    isBoldText?: boolean;
    isThickBorderTop?: boolean;
    isThickBorderBottom?: boolean;
    isThickBorderRight?: boolean;
    isThickBorderLeft?: boolean;
    hideBorderBottom?: boolean;
    hideBorderRight?: boolean;
    isGreyBackground?: boolean;
    isLightGreyBackground?: boolean;
    isSelectable?: boolean;
    hideText?: boolean;
    minValue?: number;
    maxValue?: number;
}

export interface IVZHotTableCellConfig {
    rowStart: number;
    rowEnd: number;
    colStart: number;
    colEnd: number;
    config: IVZHotTableCellConfigOptions;
}

// This class provides helper functions to style any table created with the VZHotTable component.
// The IVZHotTableCellConfigOptions interface shows all of the available options, but you can extend
// it by adding a corresponding class in the vz-hot-table.scss, and then assigning it in the cells
// callback function, in vz-hot-table-helpers.ts.
export class VZHotTableConfig {
    tableSummaryFunction: TVZHotTableSummaryFunction;
    configs: IVZHotTableCellConfig[];

    constructor() {
        this.tableSummaryFunction = "sum";
        this.configs = [];
    }

    applyConfig(config: IVZHotTableCellConfig): void {
        this.configs.push(config);
    }

    applyToRow(row: number, config: IVZHotTableCellConfigOptions): void {
        this.applyConfig({
            rowStart: row,
            rowEnd: row,
            colStart: 0,
            colEnd: Infinity,
            config,
        });
    }

    applyToColumn(col: number, config: IVZHotTableCellConfigOptions): void {
        this.applyConfig({
            rowStart: 0,
            rowEnd: Infinity,
            colStart: col,
            colEnd: col,
            config,
        });
    }

    applyToColumnAndFollowing(col: number, config: IVZHotTableCellConfigOptions): void {
        this.applyConfig({
            rowStart: 0,
            rowEnd: Infinity,
            colStart: col,
            colEnd: Infinity,
            config,
        });
    }

    applyToRowAndFollowing(row: number, config: IVZHotTableCellConfigOptions): void {
        this.applyConfig({
            rowStart: row,
            rowEnd: Infinity,
            colStart: 0,
            colEnd: Infinity,
            config,
        });
    }

    applyToTable(config: IVZHotTableCellConfigOptions): void {
        this.applyConfig({
            rowStart: 0,
            rowEnd: Infinity,
            colStart: 0,
            colEnd: Infinity,
            config,
        });
    }

    getConfig(row: number, col: number): IVZHotTableCellConfigOptions {
        let builtConfig: IVZHotTableCellConfigOptions = {};

        for (const config of this.configs) {
            if (row >= config.rowStart && row <= config.rowEnd && col >= config.colStart && col <= config.colEnd) {
                builtConfig = {
                    ...builtConfig,
                    ...config.config,
                };
            }
        }

        return builtConfig;
    }

    push(config: IVZHotTableCellConfig): void {
        this.applyConfig(config);
    }
}
